/**
 * Volugraph Studio — project-room storage layout.
 * Captures live at: users/{uid}/studio/{projectId}/captures/{captureId}/...
 *
 * DEFAULT_STUDIO_PROJECT_ID (`__default`) is a retired legacy path sentinel — do not create new
 * documents under it. Firebase Console may still show it as an implicit parent for old stray paths.
 * New users get `_studioCollectionSeed` instead. Demo captures belong in `public/{captureId}`.
 */
export const DEFAULT_STUDIO_PROJECT_ID = '__default';

/** @param {string | null | undefined} projectId */
export function isLegacyDefaultStudioProjectId(projectId) {
  return (projectId || '').trim() === DEFAULT_STUDIO_PROJECT_ID;
}

/** My Studio (all project folders) URL segment — keep in sync with App.js routes. */
export const STUDIO_MY_STUDIO_SEGMENT = 'mystudio';

/** Map workspace URL segment — `/studio/{uid}/{projectId}/map` (Cesium; separate chunk from gallery). */
export const STUDIO_MAP_SEGMENT = 'map';

/** Sidebar / stats: exclude these single segments under `/studio/:uid/...`. */
const STUDIO_UID_CHILD_RESERVED = new Set(['billing', 'settings']);

/**
 * True for `/studio/{uid}/{projectId}/map` (GIS map workspace).
 */
export function isStudioProjectMapWorkspacePath(pathname, uid) {
  if (!pathname || !uid) return false;
  const p = pathname.split('?')[0].replace(/\/$/, '') || '/';
  const m = /^\/studio\/([^/]+)\/([^/]+)\/map$/.exec(p);
  if (!m || m[1] !== uid) return false;
  if (STUDIO_UID_CHILD_RESERVED.has(m[2])) return false;
  if (m[2] === STUDIO_MY_STUDIO_SEGMENT) return false;
  return true;
}

/**
 * True for `/studio/{uid}/mystudio`, `/studio/{uid}/{projectId}`, or `/studio/{uid}/{projectId}/map`
 * (not billing/settings). Used for studio shell / nav active state.
 * False for four-segment owner capture viewer URLs and non-matching uids.
 */
export function isStudioMyStudioOrProjectPath(pathname, uid) {
  if (!pathname || !uid) return false;
  if (isStudioProjectMapWorkspacePath(pathname, uid)) return true;
  const m = /^\/studio\/([^/]+)\/([^/]+)\/?$/.exec(pathname.split('?')[0]);
  if (!m) return false;
  if (m[1] !== uid) return false;
  return !STUDIO_UID_CHILD_RESERVED.has(m[2]);
}

/**
 * True only for `/studio/{uid}/mystudio` — project folder grid (not a specific project room).
 */
export function isStudioMyStudioRootPath(pathname, uid) {
  if (!pathname || !uid) return false;
  const m = /^\/studio\/([^/]+)\/([^/]+)\/?$/.exec(pathname.split('?')[0]);
  if (!m || m[1] !== uid) return false;
  return m[2] === STUDIO_MY_STUDIO_SEGMENT;
}

/**
 * True for owner capture viewer `/studio/{uid}/{projectId}/{captureId}` (four path segments).
 * Excludes billing/settings and mystudio.
 */
export function isStudioCaptureViewerPath(pathname, uid) {
  if (!pathname || !uid) return false;
  const p = pathname.split('?')[0].replace(/\/$/, '') || '/';
  const m = /^\/studio\/([^/]+)\/([^/]+)\/([^/]+)$/.exec(p);
  if (!m || m[1] !== uid) return false;
  if (STUDIO_UID_CHILD_RESERVED.has(m[2])) return false;
  if (m[2] === STUDIO_MY_STUDIO_SEGMENT) return false;
  if (m[3] === STUDIO_MAP_SEGMENT) return false;
  return true;
}

/**
 * True for `/studio/{uid}/{projectId}` inside a project folder (not My Studio root, map workspace, billing, or settings).
 */
export function isStudioProjectRoomPath(pathname, uid) {
  return (
    isStudioMyStudioOrProjectPath(pathname, uid) &&
    !isStudioMyStudioRootPath(pathname, uid) &&
    !isStudioProjectMapWorkspacePath(pathname, uid)
  );
}

/** True when `pathname` is under `/studio/{uid}` (own or shared collaborator context). */
export function isLoggedInOwnStudioPath(pathname, uid) {
  if (!pathname || !uid) return false;
  const p = pathname.split('?')[0];
  const prefix = `/studio/${uid}`;
  return p === prefix || p.startsWith(`${prefix}/`);
}

/** Alias for studio path prefix check (used for invitee shared routes). */
export function isStudioPathForUid(pathname, uid) {
  return isLoggedInOwnStudioPath(pathname, uid);
}

/** First path segment after `/studio/` (owner or invitee routing uid). */
export function extractStudioUidFromPath(pathname) {
  if (!pathname) return null;
  const p = pathname.split('?')[0];
  const m = /^\/studio\/([^/]+)/.exec(p);
  return m ? m[1] : null;
}

/** Learn / Help docs route (logged-out uses marketing navbar; logged-in uses studio top bar). */
export function isDocumentationPath(pathname) {
  if (!pathname) return false;
  const p = pathname.split('?')[0].replace(/\/$/, '') || '/';
  return p === '/documentation' || p.startsWith('/documentation/');
}

/** Blog index and posts — same chrome behavior as Learn when inside UserLayout. */
export function isBlogPath(pathname) {
  if (!pathname) return false;
  const p = pathname.split('?')[0].replace(/\/$/, '') || '/';
  return p === '/blog' || p.startsWith('/blog/');
}

/** Learn hub or Volugraph blog under marketing/studio shell. */
export function isDocumentationOrBlogPath(pathname) {
  return isDocumentationPath(pathname) || isBlogPath(pathname);
}

/** Placeholder under studio/ so the subcollection exists (Firestore). */
export const STUDIO_SUBCOLLECTION_PLACEHOLDER_ID = '_studioCollectionSeed';


/** @param {{ id: string, data?: () => object }} doc */
export function isStudioPlaceholderDoc(doc) {
  if (!doc?.id) return true;
  if (doc.id === STUDIO_SUBCOLLECTION_PLACEHOLDER_ID) return true;
  if (doc.id === DEFAULT_STUDIO_PROJECT_ID) return true;
  const data = typeof doc.data === 'function' ? doc.data() : doc.data;
  return !!(data && data._placeholder);
}

/** Parse owner uid from `users/{ownerUid}/studio/{projectId}` doc ref or path. */
export function ownerUidFromStudioProjectRef(refOrPath) {
  const path =
    typeof refOrPath === 'string'
      ? refOrPath
      : refOrPath?.path || '';
  const segs = path.split('/').filter(Boolean);
  if (segs[0] === 'users' && segs[2] === 'studio' && segs[1]) return segs[1];
  return null;
}

export function normalizedProjectId(projectId) {
  const p = (projectId || '').trim();
  return p || DEFAULT_STUDIO_PROJECT_ID;
}

/** Real project folder id for Firestore writes — null when missing or legacy __default. */
export function resolveStudioProjectIdForWrite(projectId) {
  const p = (projectId || '').trim();
  if (!p || p === DEFAULT_STUDIO_PROJECT_ID) return null;
  return p;
}

/** `/studio/{uid}/mystudio` — root grid of project folders. */
export function studioMyStudioPath(uid) {
  if (!uid) return '/studio';
  return `/studio/${uid}/${STUDIO_MY_STUDIO_SEGMENT}`;
}

/** `/studio/{uid}/{projectId}` — captures grid inside one project. */
export function studioProjectPath(uid, projectId) {
  if (!uid) return '/studio';
  const raw = (projectId || '').trim();
  if (!raw || raw === DEFAULT_STUDIO_PROJECT_ID) return studioMyStudioPath(uid);
  return `/studio/${uid}/${raw}`;
}

/** `/studio/{uid}/{projectId}/map` — Cesium map workspace (separate route from gallery). */
export function studioProjectMapPath(uid, projectId) {
  if (!uid) return '/studio';
  const raw = (projectId || '').trim();
  if (!raw || raw === DEFAULT_STUDIO_PROJECT_ID) return studioMyStudioPath(uid);
  return `/studio/${uid}/${raw}/${STUDIO_MAP_SEGMENT}`;
}

/**
 * `/studio/{uid}/{projectId}/{captureId}` — in-app capture viewer (canonical for owners).
 * Query-only `/viewer` remains for demos, landing, and shared links.
 */
export function studioCapturePath(uid, projectId, captureId) {
  if (!uid || !(captureId || '').trim()) return studioMyStudioPath(uid);
  const pid = (projectId || '').trim();
  const cap = captureId.trim();
  if (!pid || pid === DEFAULT_STUDIO_PROJECT_ID) return studioMyStudioPath(uid);
  return `/studio/${uid}/${pid}/${cap}`;
}

export function captureStoragePrefix(uid, projectId, captureId) {
  const pid = normalizedProjectId(projectId);
  return `users/${uid}/studio/${pid}/captures/${captureId}`;
}
